import { Injectable } from '@angular/core';
import { Observable, fromEvent, map } from 'rxjs';
import { vapidKeyToBytes } from './vapid-key';

const WORKER_PATH = '/sw.js';

/**
 * The one seam onto the browser's push machinery, and it exists so WebPushService can be tested: Karma
 * reports Notification.permission as 'denied', and a real register() would leave a worker on the Karma
 * origin that survives between runs.
 */
@Injectable()
export class PushApi {
  public get supported(): boolean {
    // Notification is checked too: iOS Safari has serviceWorker without it, and the legacy implementation
    // threw a TypeError on exactly that line.
    return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
  }

  public get secureContext(): boolean {
    return window.isSecureContext;
  }

  public get permission(): NotificationPermission {
    return Notification.permission;
  }

  public requestPermission(): Promise<NotificationPermission> {
    return Notification.requestPermission();
  }

  /** updateViaCache: no hosting config has a cache rule for /sw.js, so a max-age default would pin it stale. */
  public register(): Promise<ServiceWorkerRegistration> {
    return navigator.serviceWorker.register(WORKER_PATH, { scope: '/', updateViaCache: 'none' });
  }

  public ready(): Promise<ServiceWorkerRegistration> {
    return navigator.serviceWorker.ready;
  }

  public async getSubscription(): Promise<PushSubscription | null> {
    const registration = await this.ready();
    return registration.pushManager.getSubscription();
  }

  public async subscribe(publicKey: string): Promise<PushSubscription> {
    const registration = await this.ready();
    return registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: vapidKeyToBytes(publicKey),
    });
  }

  public unsubscribe(subscription: PushSubscription): Promise<boolean> {
    return subscription.unsubscribe();
  }

  public messages(): Observable<unknown> {
    return fromEvent<MessageEvent>(navigator.serviceWorker, 'message').pipe(map((event) => event.data));
  }
}
